import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Trash2,
    Loader2,
    ChevronDown,
    ChevronRight,
    Plus,
} from "lucide-react";
import {
    updateTemplateItem,
    deleteTemplateItem,
    createTemplateItem,
} from "../lib/api";

export const PRICE_TYPES = [
    { value: "fixed", label: "Fixed" },
    { value: "per_item", label: "Per item" },
    { value: "per_length", label: "Per unit (length/area/volume/weight)" },
    { value: "per_load", label: "Per load" },
    { value: "ai_calculate", label: "AI Calculate" },
];

// Units picker used whenever a line is priced "per unit". Grouped so the
// select shows tidy Length / Area / Volume / Weight / Misc sections. The
// raw `value` is a free-form string as far as backend cares — it's just
// echoed through to reports and the printable quote.
export const LENGTH_UNITS = [
    // Length
    { value: "mm", label: "mm — Millimeters", group: "Length" },
    { value: "cm", label: "cm — Centimeters", group: "Length" },
    { value: "m", label: "m — Meters", group: "Length" },
    { value: "km", label: "km — Kilometers", group: "Length" },
    { value: "in", label: "in — Inches", group: "Length" },
    { value: "ft", label: "ft — Feet", group: "Length" },
    { value: "lf", label: "lf — Linear feet", group: "Length" },
    { value: "yd", label: "yd — Yards", group: "Length" },
    { value: "mi", label: "mi — Miles", group: "Length" },
    // Area
    { value: "m2", label: "m² — Square meters", group: "Area" },
    { value: "sqft", label: "ft² — Square feet", group: "Area" },
    { value: "sqyd", label: "yd² — Square yards", group: "Area" },
    { value: "acre", label: "ac — Acres", group: "Area" },
    { value: "hectare", label: "ha — Hectares", group: "Area" },
    // Volume (solid)
    { value: "m3", label: "m³ — Cubic meters", group: "Volume" },
    { value: "cuft", label: "ft³ — Cubic feet", group: "Volume" },
    { value: "cuyd", label: "yd³ — Cubic yards", group: "Volume" },
    // Volume (liquid)
    { value: "l", label: "L — Litres", group: "Volume · liquid" },
    { value: "gal", label: "gal — Gallons", group: "Volume · liquid" },
    // Weight
    { value: "kg", label: "kg — Kilograms", group: "Weight" },
    { value: "tonne", label: "t — Metric tonnes", group: "Weight" },
    { value: "lb", label: "lb — Pounds", group: "Weight" },
    { value: "ton", label: "ton — Short tons (2000 lb)", group: "Weight" },
    // Misc
    { value: "ea", label: "ea — Each", group: "Misc" },
    { value: "hr", label: "hr — Hours", group: "Misc" },
    { value: "day", label: "day — Days", group: "Misc" },
    { value: "pallet", label: "pallet — Pallet", group: "Misc" },
    { value: "bag", label: "bag — Bag", group: "Misc" },
];

const FIELD_CLS =
    "w-full bg-[#0A1128] border border-[#2A3655] rounded-sm px-2 py-1.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00A3FF] transition-colors";

function formatPrice(item) {
    if (item.price_type === "ai_calculate") {
        return "AI calculates";
    }
    const p = Number(item.price || 0);
    const money = `$${p.toLocaleString(undefined, { minimumFractionDigits: p % 1 === 0 ? 0 : 2, maximumFractionDigits: 2 })}`;
    if (item.price_type === "fixed") return `${money} flat`;
    if (item.price_type === "per_item") return `${money} / item`;
    if (item.price_type === "per_load") return `${money} / load`;
    return `${money} / ${item.unit || "unit"}`;
}

/**
 * Renders the editable fields + collapse/expand mechanics shared by both
 * top-level items and their sub-items. No recursion — sub-items are rendered
 * by the parent component using this same element.
 */
function ItemCard({
    item,
    onUpdated,
    onDeleted,
    onAddSub,
    addingSub,
    showAddSub,
    defaultExpanded,
    variant,
    collapseKey,
    expandKey,
}) {
    const [local, setLocal] = useState(item);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [expanded, setExpanded] = useState(!!defaultExpanded);

    useEffect(() => {
        setLocal(item);
    }, [item]);

    // Parent-driven collapse/expand signals. Each time the parent's
    // collapseKey / expandKey changes, snap this row into the corresponding
    // state. `undefined` on first mount is intentionally ignored via the
    // dependency check so we don't clobber defaultExpanded.
    useEffect(() => {
        if (collapseKey === undefined) return;
        setExpanded(false);
    }, [collapseKey]);
    useEffect(() => {
        if (expandKey === undefined) return;
        setExpanded(true);
    }, [expandKey]);

    const persist = async (patch) => {
        setSaving(true);
        try {
            const updated = await updateTemplateItem(item.id, patch);
            onUpdated(updated);
        } catch {
            toast.error("Couldn't save item");
        } finally {
            setSaving(false);
        }
    };

    const handleBlur = (field) => {
        const original = item[field] ?? "";
        const current = local[field] ?? "";
        if (String(original) !== String(current)) persist({ [field]: current });
    };

    const handleNumberBlur = (field) => {
        const original = Number(item[field] ?? 0);
        const current = Number(local[field] ?? 0);
        if (!Number.isFinite(current)) {
            setLocal((l) => ({ ...l, [field]: original }));
            return;
        }
        if (original !== current) persist({ [field]: current });
    };

    const handleSelectChange = (field, value) => {
        setLocal((l) => ({ ...l, [field]: value }));
        persist({ [field]: value });
    };

    const handleDelete = async () => {
        if (!window.confirm(`Delete "${item.name}"?`)) return;
        setDeleting(true);
        try {
            await deleteTemplateItem(item.id);
            toast.success("Item deleted");
            onDeleted();
        } catch {
            toast.error("Couldn't delete");
            setDeleting(false);
        }
    };

    const isPerLength = local.price_type === "per_length";
    const isAiCalculate = local.price_type === "ai_calculate";
    const priceSuffix =
        local.price_type === "fixed"
            ? "flat"
            : local.price_type === "per_item"
              ? "/ item"
              : local.price_type === "per_load"
                ? "/ load"
                : local.price_type === "ai_calculate"
                  ? "AI"
                  : `/ ${local.unit || "unit"}`;
    const markupSymbol = local.markup_type === "flat" ? "$" : "%";

    const containerCls =
        variant === "sub"
            ? "bg-[#0A1128]/40 border border-[#2A3655]/70 rounded-sm"
            : "bg-[#0A1128]/60 border border-[#2A3655] rounded-sm hover:border-[#3b4a75] transition-colors";

    return (
        <div className={containerCls} data-testid={`item-row-${item.id}`}>
            {/* Summary row — Planswift-style: [color bar] [name / desc] ............... [price] */}
            <div className="flex items-stretch gap-0 pr-3 py-0">
                {/* Left color accent — same swatch Planswift uses at the far right. */}
                <div
                    aria-hidden
                    data-testid={`item-color-${item.id}`}
                    className="w-1.5 shrink-0 rounded-l-sm"
                    style={{
                        backgroundColor:
                            item.color && item.color.trim()
                                ? item.color
                                : "transparent",
                    }}
                />
                <button
                    type="button"
                    onClick={() => setExpanded((v) => !v)}
                    data-testid={`item-toggle-${item.id}`}
                    aria-label={expanded ? "Collapse" : "Expand"}
                    className="shrink-0 self-center ml-1 p-1 rounded-sm text-slate-400 hover:text-white hover:bg-[#1C253C] transition-colors"
                >
                    {expanded ? (
                        <ChevronDown className="w-4 h-4" />
                    ) : (
                        <ChevronRight className="w-4 h-4" />
                    )}
                </button>
                <div className="flex-1 min-w-0 flex items-center gap-3 py-2">
                    <span
                        className={`font-semibold truncate shrink-0 ${
                            variant === "sub" ? "text-sm text-slate-200" : "text-sm text-white"
                        }`}
                        title={
                            item.updated_by_name
                                ? `${item.name}\nLast edited by ${item.updated_by_name} · ${_relTime(item.updated_at)}`
                                : item.name
                        }
                        data-testid={`item-name-summary-${item.id}`}
                    >
                        {item.name || <span className="italic text-slate-500">Unnamed</span>}
                    </span>
                    {item.description ? (
                        <span
                            className="flex-1 min-w-0 truncate text-xs text-slate-400"
                            title={item.description}
                            data-testid={`item-desc-summary-${item.id}`}
                        >
                            {item.description}
                        </span>
                    ) : (
                        <span className="flex-1" />
                    )}
                    {item.updated_by_name && (
                        <span
                            className="text-[10px] text-slate-500 tracking-tight truncate hidden md:inline shrink-0"
                            data-testid={`item-editor-byline-${item.id}`}
                            title={`Last edited by ${item.updated_by_name} at ${item.updated_at}`}
                        >
                            edited by {item.updated_by_name.split(" ")[0]} {_relTime(item.updated_at)}
                        </span>
                    )}
                </div>
                {/* Right-aligned price column + markup badge */}
                <div className="flex items-center gap-2 shrink-0 py-2 pl-4 tabular-nums">
                    {Number(local.markup_value) > 0 && (
                        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold hidden sm:inline">
                            +{local.markup_value}
                            {markupSymbol}
                        </span>
                    )}
                    <span
                        className="text-sm text-[#00A3FF] font-bold text-right min-w-[6.5rem]"
                        data-testid={`item-price-summary-${item.id}`}
                    >
                        {formatPrice(local)}
                    </span>
                </div>
                {saving && (
                    <Loader2
                        className="w-3.5 h-3.5 animate-spin text-slate-500 shrink-0 self-center"
                        data-testid={`item-saving-${item.id}`}
                    />
                )}
                {showAddSub && (
                    <button
                        type="button"
                        onClick={onAddSub}
                        disabled={addingSub}
                        data-testid={`item-add-sub-${item.id}`}
                        title="Add a sub-item under this item"
                        aria-label="Add sub-item"
                        className="shrink-0 p-1.5 rounded-sm text-slate-400 hover:bg-[#00A3FF]/10 hover:text-[#00A3FF] transition-colors disabled:opacity-50"
                    >
                        {addingSub ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                            <Plus className="w-4 h-4" />
                        )}
                    </button>
                )}
                <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    data-testid={`item-delete-${item.id}`}
                    aria-label="Delete item"
                    className="shrink-0 p-1.5 rounded-sm text-slate-400 hover:bg-red-500/20 hover:text-red-300 transition-colors disabled:opacity-50"
                >
                    {deleting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Trash2 className="w-4 h-4" />
                    )}
                </button>
            </div>

            {/* Expanded editor */}
            {expanded && (
                <div
                    className="grid grid-cols-12 gap-2 p-3 border-t border-[#2A3655]/70"
                    data-testid={`item-editor-${item.id}`}
                >
                    <div className="col-span-12 md:col-span-4 space-y-1.5">
                        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block">
                            Name
                        </label>
                        <input
                            type="text"
                            value={local.name}
                            onChange={(e) => setLocal((l) => ({ ...l, name: e.target.value }))}
                            onBlur={() => handleBlur("name")}
                            placeholder='e.g. Trench, 24" deep'
                            className={FIELD_CLS}
                            data-testid={`item-name-${item.id}`}
                        />
                        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block pt-1">
                            Description
                        </label>
                        <textarea
                            rows={2}
                            value={local.description || ""}
                            onChange={(e) =>
                                setLocal((l) => ({ ...l, description: e.target.value }))
                            }
                            onBlur={() => handleBlur("description")}
                            placeholder="Optional notes the AI should know about this item"
                            className={`${FIELD_CLS} resize-none`}
                            data-testid={`item-desc-${item.id}`}
                        />
                    </div>

                    <div className="col-span-12 md:col-span-4 space-y-1.5">
                        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block">
                            Price
                        </label>
                        <div className="flex items-stretch gap-1">
                            <div className="relative flex-1">
                                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500 text-sm">
                                    $
                                </span>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    // Show blank instead of "0" when the price hasn't been set
                                    // yet — placeholder "0.00" cues the user. Once they type
                                    // anything the value sticks. Empty on blur falls back to 0.
                                    value={
                                        local.price === 0
                                            || local.price === "0"
                                            || local.price === null
                                            || local.price === undefined
                                            ? ""
                                            : local.price
                                    }
                                    onChange={(e) =>
                                        setLocal((l) => ({ ...l, price: e.target.value }))
                                    }
                                    onBlur={() => handleNumberBlur("price")}
                                    placeholder={isAiCalculate ? "AI will set" : "0.00"}
                                    disabled={isAiCalculate}
                                    className={`${FIELD_CLS} pl-5 tabular-nums ${isAiCalculate ? "opacity-50 cursor-not-allowed" : ""}`}
                                    data-testid={`item-price-${item.id}`}
                                />
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold self-center px-1 whitespace-nowrap">
                                {priceSuffix}
                            </div>
                        </div>

                        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block pt-1">
                            Pricing mode
                        </label>
                        <div className="flex gap-1">
                            <select
                                value={local.price_type}
                                onChange={(e) =>
                                    handleSelectChange("price_type", e.target.value)
                                }
                                className={`${FIELD_CLS} flex-1`}
                                data-testid={`item-price-type-${item.id}`}
                            >
                                {PRICE_TYPES.map((p) => (
                                    <option key={p.value} value={p.value}>
                                        {p.label}
                                    </option>
                                ))}
                            </select>
                            {isPerLength && (
                                <select
                                    value={local.unit || "ft"}
                                    onChange={(e) => handleSelectChange("unit", e.target.value)}
                                    className={`${FIELD_CLS} flex-1`}
                                    data-testid={`item-unit-${item.id}`}
                                >
                                    {Object.entries(
                                        LENGTH_UNITS.reduce((acc, u) => {
                                            const g = u.group || "Other";
                                            (acc[g] ||= []).push(u);
                                            return acc;
                                        }, {}),
                                    ).map(([group, units]) => (
                                        <optgroup key={group} label={group}>
                                            {units.map((u) => (
                                                <option key={u.value} value={u.value}>
                                                    {u.label}
                                                </option>
                                            ))}
                                        </optgroup>
                                    ))}
                                </select>
                            )}
                        </div>
                    </div>

                    <div className="col-span-12 md:col-span-4 space-y-1.5">
                        <label className="text-[10px] uppercase tracking-wider text-slate-500 font-bold block">
                            Add profit
                            <span className="text-slate-500 font-normal normal-case ml-1">
                                (added on top)
                            </span>
                        </label>
                        <div className="flex items-stretch w-full rounded-sm border border-[#2A3655] bg-[#0A1128] focus-within:border-[#00A3FF] overflow-hidden">
                            <span
                                className="flex items-center justify-center w-8 shrink-0 text-slate-400 text-sm border-r border-[#2A3655] bg-[#0F1B3A] tabular-nums select-none"
                                aria-hidden="true"
                            >
                                {markupSymbol}
                            </span>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                value={local.markup_value ?? 0}
                                onChange={(e) =>
                                    setLocal((l) => ({
                                        ...l,
                                        markup_value: e.target.value,
                                    }))
                                }
                                onFocus={(e) => e.currentTarget.select()}
                                onBlur={() => handleNumberBlur("markup_value")}
                                placeholder="0"
                                inputMode="decimal"
                                className="flex-1 min-w-0 bg-transparent border-0 outline-none px-2 py-1.5 text-sm tabular-nums text-white placeholder:text-slate-600"
                                data-testid={`item-markup-value-${item.id}`}
                            />
                            <div
                                className="flex items-center shrink-0 border-l border-[#2A3655] p-0.5 gap-0.5 bg-[#0F1B3A]"
                                role="group"
                                aria-label="Add profit unit"
                            >
                                <button
                                    type="button"
                                    onClick={() =>
                                        handleSelectChange("markup_type", "percent")
                                    }
                                    data-testid={`item-markup-type-percent-${item.id}`}
                                    className={`px-2 py-1 text-xs font-bold rounded-sm transition-colors ${
                                        local.markup_type === "percent"
                                            ? "bg-[#00A3FF] text-white"
                                            : "text-slate-400 hover:text-white"
                                    }`}
                                    aria-pressed={local.markup_type === "percent"}
                                >
                                    %
                                </button>
                                <button
                                    type="button"
                                    onClick={() =>
                                        handleSelectChange("markup_type", "flat")
                                    }
                                    data-testid={`item-markup-type-flat-${item.id}`}
                                    className={`px-2 py-1 text-xs font-bold rounded-sm transition-colors ${
                                        local.markup_type === "flat"
                                            ? "bg-[#00A3FF] text-white"
                                            : "text-slate-400 hover:text-white"
                                    }`}
                                    aria-pressed={local.markup_type === "flat"}
                                >
                                    $
                                </button>
                            </div>
                        </div>
                        <p className="text-[10px] text-slate-500 pt-1">
                            Profit added on top of base price.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}

export default function TemplateItemRow({
    item,
    onUpdated,
    onDeleted,
    subItems = [],
    onSubItemCreated,
    onSubItemUpdated,
    onSubItemDeleted,
    defaultExpanded = false,
    collapseKey,
    expandKey,
}) {
    const [addingSub, setAddingSub] = useState(false);

    const handleAddSubItem = async () => {
        setAddingSub(true);
        try {
            const created = await createTemplateItem(item.category_id, {
                name: "New sub-item",
                parent_item_id: item.id,
            });
            if (onSubItemCreated) onSubItemCreated(created);
        } catch {
            toast.error("Couldn't add sub-item");
        } finally {
            setAddingSub(false);
        }
    };

    return (
        <div className="space-y-2">
            <ItemCard
                item={item}
                onUpdated={onUpdated}
                onDeleted={onDeleted}
                onAddSub={handleAddSubItem}
                addingSub={addingSub}
                showAddSub
                defaultExpanded={defaultExpanded}
                variant="top"
                collapseKey={collapseKey}
                expandKey={expandKey}
            />
            {subItems.length > 0 && (
                <div
                    className="space-y-2 ml-6 pl-3 border-l border-[#2A3655]/70"
                    data-testid={`item-subitems-${item.id}`}
                >
                    {subItems.map((sub) => (
                        <ItemCard
                            key={sub.id}
                            item={sub}
                            onUpdated={(updated) => onSubItemUpdated(sub.id, updated)}
                            onDeleted={() => onSubItemDeleted(sub.id)}
                            variant="sub"
                            showAddSub={false}
                            collapseKey={collapseKey}
                            expandKey={expandKey}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function _relTime(iso) {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return "";
    const delta = Math.max(0, Date.now() - then);
    const m = Math.floor(delta / 60_000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    const w = Math.floor(d / 7);
    return `${w}w ago`;
}

